import React, { useState } from 'react';
import SocialMediaIcons from './SocialMediaIcons';
import ProviderBioTrigger from './ProviderBioTrigger';
import ProviderBioReadMore from './ProviderBioReadMore';
import ImageLightbox from './ImageLightbox';
import { getBookingPageTheme } from '../../utility/themeUtils';
import { isDarkMode } from '../../utility/themeVars';

/**
 * Hero card shown on the services step only. Renders a single layout driven
 * entirely by what the provider has configured — no mode heuristic:
 *
 * - **Heading**: `businessName` if set, else `providerName`. Business wins
 *   when both are set.
 * - **"with {providerName}" subtitle**: shown only when both names are set
 *   AND they differ (trimmed, case-insensitive) — i.e. only to distinguish
 *   two different identities. Suppressed when the names match or there's no
 *   business name.
 * - **Business tagline**: `businessTagline` renders as a short caption under
 *   the heading whenever it's present, independent of which name is shown.
 * - **Provider bio**: surfaced via a `ProviderBioTrigger` ("Meet
 *   {providerName}") chip only when a business name is the heading **and** a
 *   provider name exists to label the chip; otherwise surfaced inline via
 *   `ProviderBioReadMore`. The bio is never dropped — the provider name only
 *   decides how it's surfaced, not whether.
 * - **Primary image**: `logoUrl` (falls back to `profilePhotoUrl`) when a
 *   business identity is shown as the heading; `profilePhotoUrl` (falls back
 *   to `logoUrl`) when the provider is the primary identity.
 *
 * A `hasAnyContent` guard (image, heading, tagline, bio, contact, social)
 * means a fully-empty booking page renders nothing.
 *
 * @param {Object} bookingPage - Booking page data object.
 * @param {string} primaryColor - Tailwind color token, e.g. 'blue-600'.
 */
const PublicBookingHero = ({ bookingPage, primaryColor }) => {
  const { themeMode } = getBookingPageTheme(bookingPage);
  const isDark = isDarkMode(themeMode);

  // The primary hero image currently open in the lightbox ({ url, alt }), or
  // null when closed.
  const [heroImage, setHeroImage] = useState(null);
  const openHeroImage = (url, alt) => setHeroImage({ url, alt });

  const logoUrl = bookingPage?.logoUrl || null;
  const profilePhotoUrl = bookingPage?.profilePhotoUrl || null;
  const businessName = bookingPage?.businessName?.trim() || null;
  const providerName = bookingPage?.providerName?.trim() || null;
  const businessTagline = bookingPage?.businessTagline?.trim() || null;
  const providerBio = bookingPage?.providerBio || null;
  const bannerImageUrl = bookingPage?.bannerImageUrl || null;

  // Business identity is the heading whenever businessName is set — it wins
  // over providerName when both exist.
  const showBusinessAsHeading = !!businessName;
  const heading = businessName || providerName;

  // The "with {providerName}" subtitle only earns its place when it
  // distinguishes a second, different identity from the heading.
  const namesDiffer =
    !!businessName &&
    !!providerName &&
    businessName.toLowerCase() !== providerName.toLowerCase();
  const subtitle = namesDiffer ? providerName : null;

  // Primary image preference follows which identity is shown as the heading.
  const primaryImageUrl = showBusinessAsHeading
    ? logoUrl || profilePhotoUrl || null
    : profilePhotoUrl || logoUrl || null;
  const primaryImageAlt =
    heading || (showBusinessAsHeading ? 'Business logo' : 'Profile photo');

  // The "Meet {name}" chip only makes sense with a provider name to name it.
  // When a business name is the heading AND a provider name exists, tuck the bio
  // behind the chip. Otherwise (provider is the heading, OR there's a bio but no
  // provider name) surface the bio inline so it's never dropped.
  const showBioTrigger = showBusinessAsHeading && !!providerName && !!providerBio;
  const showInlineBio = !showBioTrigger && !!providerBio;

  // Contact info fields
  const showContactRow =
    bookingPage?.showContactInfo &&
    (bookingPage?.contactPhone || bookingPage?.contactEmail || bookingPage?.contactAddress);

  // Social media row
  const showSocialRow =
    bookingPage?.showSocialMedia && bookingPage?.socialMediaLinks;

  const hasAnyContent =
    bannerImageUrl ||
    primaryImageUrl ||
    heading ||
    businessTagline ||
    providerBio ||
    showContactRow ||
    showSocialRow;

  if (!hasAnyContent) {
    return null;
  }

  const heroLightbox = (
    <ImageLightbox
      open={!!heroImage}
      onClose={() => setHeroImage(null)}
      imageUrl={heroImage?.url}
      alt={heroImage?.alt}
      primaryColor={primaryColor}
      themeMode={themeMode}
    />
  );

  return (
    <>
      <div className="bg-themed-card border-themed rounded-lg shadow-lg border overflow-hidden">
        {/* Banner image */}
        <Banner
          bannerImageUrl={bannerImageUrl}
          alt={heading ? `${heading} banner image` : ''}
        />

        <div className="py-8 px-6 sm:px-8 space-y-6">
          {/* Primary mark row: image + heading + optional "with" subtitle */}
          {(primaryImageUrl || heading) && (
            <div className="flex items-center gap-5">
              {primaryImageUrl && (
                <button
                  type="button"
                  onClick={() => openHeroImage(primaryImageUrl, primaryImageAlt)}
                  aria-label={`View larger ${primaryImageAlt}`}
                  className="group relative flex-shrink-0 rounded-full cursor-zoom-in focus:outline-none focus-themed-ring"
                >
                  <img
                    src={primaryImageUrl}
                    alt={primaryImageAlt}
                    className={`w-24 h-24 rounded-full object-cover border-4 shadow-md transition group-hover:brightness-95 ${
                      isDark ? 'border-ink-600' : 'border-white'
                    }`}
                  />
                </button>
              )}
              <div className="min-w-0">
                {heading && (
                  <h1 className="font-themed-display text-3xl sm:text-4xl text-themed-primary leading-[1.05] break-words">
                    {heading}
                  </h1>
                )}
                {subtitle && (
                  <p className="text-sm text-themed-secondary mt-1 break-words">
                    with {subtitle}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Business tagline — short caption under the heading */}
          {businessTagline && (
            <p className="text-themed-secondary leading-relaxed whitespace-pre-line">
              {businessTagline}
            </p>
          )}

          {/* Bio block — clamped to 3 lines with "Read more" when truncated,
              inline only when the provider is the primary identity */}
          {showInlineBio && (
            <ProviderBioReadMore
              providerBio={providerBio}
              bookingPage={bookingPage}
            />
          )}

          {/* Contact icon row */}
          {showContactRow && <ContactRow bookingPage={bookingPage} isDark={isDark} />}

          {/* Social icon row */}
          {showSocialRow && (
            <SocialMediaIcons
              socialMediaLinks={bookingPage.socialMediaLinks}
              primaryColor={primaryColor}
              className="flex-wrap gap-4"
            />
          )}
        </div>
      </div>

      {/* Provider bio trigger chip — rendered below the hero card when the
          business identity is shown as the heading. Self-guards on
          providerName/providerBio. */}
      {showBioTrigger && <ProviderBioTrigger bookingPage={bookingPage} />}

      {heroLightbox}
    </>
  );
};

// ── Shared banner ─────────────────────────────────────────────────────────────

/**
 * Reusable banner image at the top of the hero card.
 *
 * The banner height follows the uploaded image: the image spans the full card
 * width (`w-full`) and its height tracks its natural ratio (`h-auto`), so a wide
 * image renders short (less space) and a squarer image renders taller. Height is
 * capped at `max-h-[378px]` (≈ the previous fixed 16:9 height at the 672px desktop
 * card) so a tall image can't balloon. `object-cover` keeps the image filling the
 * card width edge-to-edge with no side gaps; only a tall image that hits the cap
 * is cropped top/bottom.
 */
const Banner = ({ bannerImageUrl, alt }) => {
  if (!bannerImageUrl) return null;
  return (
    <div className="relative w-full overflow-hidden">
      <img
        src={bannerImageUrl}
        alt={alt}
        className="w-full h-auto object-cover max-h-[378px]"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
    </div>
  );
};

// ── Shared contact row ────────────────────────────────────────────────────────

/**
 * Reusable contact icon row: phone, email, address.
 */
const ContactRow = ({ bookingPage, isDark }) => (
  <div className="flex flex-wrap gap-x-6 gap-y-3">
    {bookingPage.contactPhone && (
      <a
        href={`tel:${bookingPage.contactPhone}`}
        className="flex items-center gap-2 text-themed-secondary hover:text-themed-primary transition-colors text-sm"
      >
        {/* Phone icon */}
        <svg
          className="w-4 h-4 flex-shrink-0 text-ink-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
          />
        </svg>
        <span className="hidden sm:inline">{bookingPage.contactPhone}</span>
        <span className="sr-only sm:hidden">{bookingPage.contactPhone}</span>
      </a>
    )}

    {bookingPage.contactEmail && (
      <a
        href={`mailto:${bookingPage.contactEmail}`}
        className="flex items-center gap-2 text-themed-secondary hover:text-themed-primary transition-colors text-sm min-w-0"
      >
        {/* Email icon */}
        <svg
          className="w-4 h-4 flex-shrink-0 text-ink-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
        <span className="hidden sm:inline truncate">{bookingPage.contactEmail}</span>
        <span className="sr-only sm:hidden">{bookingPage.contactEmail}</span>
      </a>
    )}

    {bookingPage.contactAddress && (
      <a
        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(bookingPage.contactAddress)}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open directions to ${bookingPage.contactAddress} in a new tab`}
        className="flex items-start gap-2 text-themed-secondary hover:text-themed-primary transition-colors text-sm"
      >
        <svg
          className="w-4 h-4 flex-shrink-0 text-ink-400 mt-0.5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="hidden sm:inline">{bookingPage.contactAddress}</span>
        <span className="sm:hidden">Directions</span>
      </a>
    )}
  </div>
);

export default PublicBookingHero;
